/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import dayjs from 'dayjs';
import template from './tee-table.stache';
import can from 'can';
import addtocalendar from 'vendors/addtocalendar/addtocalendar';
import dtm from 'scripts/services/dtm';

const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';
const dateTimeParsingFormat = 'YYYY-MM-DDTHH:mm:ss';
const defaultTimezone = window.globalSettings.defaultTimezone;
const playerPageUrl = window.globalSettings.pagePaths.player;
const courseLocationCity = window.globalSettings.courseLocationCity;
const courseName = window.globalSettings.courseName;
const courseLocationState = window.globalSettings.courseLocationState;

Component.extend({
    tag: 'tee-table',
    template,
    viewModel: {
        define: {
            playerGroups: {
                Type: can.List,
                Value: can.List,
            },
        },
        sortBy: 'time',
        ordering: 'asc',
        timeFormat: 'origin',
        city: courseLocationCity,
        courseName: courseName,
        courseLocationState: courseLocationState,

        getPlayersString(players) {
            if (players) {
                const arr = [];
                players.forEach((item) => {
                    arr.push(item.lastName);
                });

                return arr.join('/');
            }
        },

        formatTime(date) {
            const timeFormat = this.attr('timeFormat');

            if (timeFormat === 'origin') {
                return this.getOriginDateTime(date).format(dateTimeFormat);
            } else {
                return dayjs(date).format(dateTimeFormat);
            }
        },

        getTime(date) {
            const timeFormat = this.attr('timeFormat');

            if (timeFormat === 'origin') {
                return this.getOriginDateTime(date).format('h:mm');
            } else {
                return dayjs(date).format('h:mm');
            }
        },

        getMeridian(date) {
            const timeFormat = this.attr('timeFormat');

            if (timeFormat === 'origin') {
                return this.getOriginDateTime(date).format('A');
            } else {
                return dayjs(date).format('A');
            }
        },

        showCurrentHole(group) {
            return (
                !Number.isNaN(Number.parseInt(group.players.currentHole)) &&
                group.isLocationAvailable
            );
        },

        getTimezone(timeFormat) {
            if (timeFormat === 'origin') {
                const { name } = this.getDefaultTimezone();
                return name;
            } else {
                return dayjs.tz.guess();
            }
        },

        getTimezoneAbbr(timeFormat) {
            if (timeFormat !== 'origin') {
                const timeZone = dayjs.tz.guess();
                return dayjs()
                    .tz(timeZone)
                    .format('zzz')
                    .split(' ')
                    .reduce((acc, el) => acc + el[0], '');
            }

            const { abbr } = this.getDefaultTimezone();

            return abbr;
        },

        getDefaultTimezone() {
            return defaultTimezone;
        },

        formatPlayerGroups(playerGroups) {
            return playerGroups.map((group) => {
                return {
                    group,
                    visible: true,
                };
            });
        },

        getOriginDateTime(date) {
            const { name } = this.getDefaultTimezone();
            return dayjs(date).tz(name);
        },

        sortByTime() {
            const playerGroups = this.attr('playerGroups');
            const ordering = this.attr('ordering');

            playerGroups.attr('comparator', (a, b) => {
                const date1 = dayjs(a.group.date).valueOf();
                const date2 = dayjs(b.group.date).valueOf();

                if (ordering === 'asc') {
                    return date1 === date2 ? 0 : date1 > date2 ? 1 : -1;
                } else {
                    return date1 === date2 ? 0 : date1 > date2 ? -1 : 1;
                }
            });

            this.attr('sortBy', 'time');
        },

        sortByTee() {
            const playerGroups = this.attr('playerGroups');
            const ordering = this.attr('ordering');

            playerGroups.attr('comparator', (a, b) => {
                const tee1 = a.group.tee;
                const tee2 = b.group.tee;

                if (ordering === 'asc') {
                    return tee1 === tee2 ? 0 : tee1 > tee2 ? 1 : -1;
                } else {
                    return tee1 === tee2 ? 0 : tee1 > tee2 ? -1 : 1;
                }
            });

            this.attr('sortBy', 'tee');
        },
        filterPlayers() {
            const query = this.attr('searchValue').toLowerCase();
            const playerGroups = this.attr('playerGroups');
            let visiblePlayerGroupsCount = 0;

            playerGroups.forEach((playerGroup) => {
                let isVisibleCount = 0;
                (playerGroup.attr('group.players.players') || []).forEach(
                    (player) => {
                        const isMatchFirstName =
                            player.firstName.toLowerCase().indexOf(query) === 0;
                        const isMatchLastName =
                            player.lastName.toLowerCase().indexOf(query) === 0;
                        const isMatchFirstAndLastName =
                            `${player.firstName} ${player.lastName}`
                                .toLowerCase()
                                .indexOf(query) === 0;
                        const isMatchLastNameAndFirst =
                            `${player.lastName} ${player.firstName}`
                                .toLowerCase()
                                .indexOf(query) === 0;

                        if (
                            isMatchFirstName ||
                            isMatchLastName ||
                            isMatchFirstAndLastName ||
                            isMatchLastNameAndFirst
                        ) {
                            isVisibleCount++;
                        }
                    },
                );
                playerGroup.attr('visible', !!isVisibleCount);

                if (!!isVisibleCount) {
                    visiblePlayerGroupsCount++;
                }
            });

            this.attr('visiblePlayerGroupsCount', visiblePlayerGroupsCount);

            if (this.attr('sortBy') === 'time') {
                this.sortByTime();
            } else {
                this.sortByTee();
            }
        },
        removeCalendars() {
            const calendars = this.$table.find('.addtocalendar .atcb-list');
            calendars.remove();
        },
        onLastGroupRendered() {
            // Load calendar in next tick when playerGroups are rendered
            setTimeout(() => {
                this.removeCalendars();
                window.addtocalendar.load();
            }, 0);
        },
        playerDetailLink(playerId) {
            return can.sub(playerPageUrl, { playerId });
        },
    },
    events: {
        inserted(element) {
            this.viewModel.bind('currentRound', () => {
                this.init(element);
            });
            this.viewModel.bind('searchValue', () => {
                this.viewModel.filterPlayers();
            });

            this.init(element);
        },
        init(element) {
            this.table = element;

            this.viewModel.$table = element;

            if (
                this.viewModel.attr('currentRound') &&
                this.viewModel.attr('currentRound.items')
            ) {
                can.batch.start();

                this.initTable();

                can.batch.stop();
            }

            this.switchItems = element.find('.s-switcher-item');
            this.dropdownCalendarItems = element.find('.s-calendar-dropdown');
        },
        initTable() {
            this.viewModel.attr(
                'playerGroups',
                this.viewModel.formatPlayerGroups(
                    this.viewModel.attr('currentRound.items'),
                ),
            );
            this.viewModel.attr(
                'visiblePlayerGroupsCount',
                this.viewModel.playerGroups.length,
            );
            this.viewModel.attr(
                'playerGroupsBoundary',
                this.viewModel.visiblePlayerGroupsCount
                    ? this.viewModel.visiblePlayerGroupsCount - 1
                    : 0,
            );

            if (this.viewModel.attr('sortBy') === 'time') {
                this.viewModel.sortByTime();
            } else {
                this.viewModel.sortByTee();
            }
        },
        '.s-switcher-item click'(element) {
            this.viewModel.attr('timeFormat', element.attr('data-index'));
        },
        '.s-switcher-line click'(element) {
            const timeFormat = this.viewModel.attr('timeFormat');

            this.viewModel.attr(
                'timeFormat',
                timeFormat === 'origin' ? 'local' : 'origin',
            );
        },
        '.s-trow--headers .s-td--time click'() {
            if (this.viewModel.attr('ordering') === 'asc') {
                this.viewModel.attr('ordering', 'desc');
            } else {
                this.viewModel.attr('ordering', 'asc');
            }
            this.viewModel.sortByTime();
        },
        '.s-calendar-btn click'(element) {
            const calendarButtons = this.table.find('.s-calendar-btn');
            const $dropdownElements = this.table.find('.js-calendar-dropdown');
            const $dropdownEl = element.parent().find('.js-calendar-dropdown');

            if ($dropdownEl.hasClass('is-expanded')) {
                $dropdownEl.removeClass('is-expanded');
                calendarButtons.attr('aria-expanded', false);
            } else {
                $dropdownElements.removeClass('is-expanded');
                calendarButtons.attr('aria-expanded', false);
                $dropdownEl.addClass('is-expanded');
                element.attr('aria-expanded', true);
                dtm.trackTeeTimesCalendar();
            }
        },
        '{document} click'(element, event) {
            if (this.table) {
                let $dropdownEl = this.table.find('.js-calendar-dropdown');

                if ($(event.target).closest('.s-calendar-btn').length === 0) {
                    if (
                        $(event.target).closest('.addtocalendar-link')
                            .length === 0
                    ) {
                        $dropdownEl.removeClass('is-expanded');
                    }
                }
            }
        },
    },
});
